import React, { useMemo } from 'react';
import IconToolTip from '../../../common/IconToolTip';
import { useDispatch } from 'react-redux';
import { copyToClipboard } from '../../../actions/utils';
import StatusLabel from '../../Payments/reusableComponents/StatusLabel';
import { getLookupWitHDefaultValue } from '../../../utils/common';
import Checkbox from '../../../common/Checkbox';
import { MilestoneType, TicketSource } from '../TicketController';


export const cityTierClass = getLookupWitHDefaultValue({
    lookup: {
        TIER1: 'scogoclosed',
        TIER2: 'scogoorange',
        TIER3: 'scogoBasicYellow',
        TIER4: 'scogobgsky',
    },
    defaultValue: () => 'scogoclosed',
});

export const getCurrentMilestone = (ticket) => {
    // Define status priority order
    const statusOrder = [
        'NEW',
        'REMOTE_ASSIGNED',
        'FIELD_ASSIGNED',
        'BROADCASTED',
        'FIELD_ACCEPTED',
        'TRACKING_REQUESTED',
        'ENGINEER_ON_THE_WAY',
        'ENGINEER_AT_SITE',
        'ENGINEER_LEFT_SITE',
        'SIGN_OFF_REQUESTED',
        'SIGN_OFF_REJECTED',
        'SIGN_OFF_ACCEPTED',
        'SITE_COMPLETED',
        'HARD_CLOSED'
    ];

    // Check for each status in order
    for (const status of statusOrder) {
        const milestone = ticket.milestones.find(m =>
            m.milestone_status === status
        );
        if (milestone) return milestone;
    }

    const undefinedMilestone = ticket.milestones.find(m =>
        !m.milestone_status
    );
    if (undefinedMilestone) return undefinedMilestone;

    return ticket.milestones[0];
}

export default function SiteColumn({ ticket, showCheckBox, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal }) {
    const dispatch = useDispatch();

    const copySiteAddress = (address) => {
        dispatch(copyToClipboard({ data: `${address}` }));
    }

    const localContact = useMemo(() => {
        let mobile = '';
        let name = '';
        if (ticket?.owner?.name) {
            name = ticket.owner.name;
            mobile = ticket.owner.mobile;
        } else if (ticket?.location?.owners?.length > 0) {
            name = ticket?.location?.owners[0].name || '';
            mobile = ticket?.location?.owners[0].mobile || '';
        } else if (ticket.source === TicketSource.IVR) {
            if (ticket.requester_phone_number || ticket.requester_name) {
                mobile = ticket.requester_phone_number || '';
                name = ticket.requester_name || ''
            }
        }
        if (!name && !mobile) return ''
        return `${name} ${mobile ? `, ${mobile}` : ''}`;
    }, [ticket?.location?.owners, ticket.source, ticket.requester_phone_number, ticket.requester_name, ticket?.owner]);


    const locationData = useMemo(() => {
        let tier = '';
        let city = '';
        let pincode = ''
        if (ticket?.location?.regions) {
            const findRegion = ticket?.location?.regions.find((elem) => elem.region_tier);
            const findCity = ticket?.location?.regions.find((elem) => elem.region_key === 'city');
            const fPincode = ticket?.location?.regions.find((elem) => elem.region_key === 'pin');
            if (findRegion) {
                tier = findRegion.region_tier;
            }
            if (findCity) {
                city = findCity.region_name;
            }
            if (fPincode) {
                pincode = fPincode.region_name;
            }
        }
        return { tier, city, pincode };
    }, [ticket?.location?.regions]);

    const isOnlyLogistics = useMemo(() => {
        if (ticket.milestones.length > 0) {
            return ticket.milestones.every(elem => elem.type === MilestoneType.LOGISTICS);
        }
        return false;
    }, [ticket.milestones]);

    if (isOnlyLogistics) {
        return <SiteForLogistics ticket={ticket} copySiteAddress={copySiteAddress} locationData={locationData} localContact={localContact} />
    }

    return (<div className="w-full flex break-all">
        {showCheckBox ? <div className='max-w-max pl-2 items-center flex'>
            <Checkbox
                checkColor='text-scogoorange mr-2'
                checked={checkedTicketTrackerLocal.has(rowIndex)}
                onChange={() => {
                    handleOnChangeCheckboxLocal(rowIndex);
                }}
                dynamicSize={'1.2rem'}
            />
        </div> : <></>}
        <div className='w-11/12'>
            <div className='flex w-full gap-1'>
                {ticket?.location?.external_id && (
                    <IconToolTip title={ticket?.location?.external_id} placement='bottom'>
                        <p className={`cursor-pointer truncate  text-font10 text-black`}>
                            Site Id : <span className={' text-black w-full font-medium text-font11'}>{ticket?.location?.external_id}</span>
                        </p>
                    </IconToolTip>
                )}

                {ticket?.location?.external_id?.length === 0 && ticket?.location?.branch?.name?.length === 0 && (
                    <p className={`cursor-pointer truncate text-font10 text-black`}>Site Id :</p>
                )}

                {ticket?.location?.external_id && (
                    <IconToolTip placement={'bottom'} title={`Copy Site Id`}>
                        <span
                            className='cursor-pointer material-icons text-scogobgsky text-lg'
                            onClick={() => dispatch(copyToClipboard({ data: `${ticket?.location?.external_id}` }))}
                        >
                            content_copy
                        </span>
                    </IconToolTip>
                )}
            </div>
            <div>
                {ticket?.location?.branch?.name && (ticket?.location?.branch?.name !== ticket?.location?.external_id) ? (
                    <p className={`cursor-pointer truncate  text-font10 text-scogoorange`}>
                        Branch : <span className={'text-scogobgsky w-full text-font10'}>{ticket?.location?.branch?.name}</span>
                    </p>
                ) : null}
            </div>
            {localContact &&
                <IconToolTip placement={'bottom'} title={localContact}>
                    <div className='text-scogobgsky line-clamp-1 cursor-pointer text-left text-font10 hover:text-scogoorange'>
                        <span className={'text-scogoorange'}>LC : </span>{localContact}
                    </div>
                </IconToolTip>
            }

            <div className='w-full'>
                {ticket?.location?.address && <IconToolTip placement={'bottom'} title={<SiteDetailsPopup currentMilestone={ticket} copySiteAddress={copySiteAddress} dispatch={dispatch} localContact={localContact} locationData={locationData} />}>
                    <span className='text-scogogray text-font11 italic line-clamp-2'>
                        {ticket?.location?.address}
                    </span>
                </IconToolTip>}

                <div className='w-full flex '>
                    {(locationData.city || locationData.pincode) ? <IconToolTip title='Pincode, City'>
                        <span className='text-black text-font11 max-w-8/12 truncate'>{`${locationData.pincode}, ${locationData.city}`}</span>
                    </IconToolTip> : null}
                    {(ticket?.location?.address) && (
                        <>
                            <IconToolTip placement={'bottom'} title={`Open Location`}>

                                <span className='cursor-pointer material-icons text-scogobgsky text-lg' onClick={() => {
                                    window.open(`https://maps.google.com?q=${`${ticket?.location?.address}`}`, '_blank');
                                }}>
                                    place
                                </span>
                            </IconToolTip>
                            <IconToolTip placement={'bottom'} title={`Copy Address`}>
                                <span
                                    className='cursor-pointer material-icons text-scogobgsky text-lg'
                                    onClick={() => copySiteAddress(ticket?.location?.address)}
                                >
                                    content_copy
                                </span>
                            </IconToolTip>
                        </>
                    )}
                    {locationData.tier && (
                        <span className='w-4/12 truncate '>
                            <StatusLabel
                                titleText='City Tier'
                                labelClass={'bg-scogolightblue'}
                                content={locationData.tier}
                                color={cityTierClass[locationData.tier]}
                            />
                        </span>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
}


const SiteDetailsPopup = ({ currentMilestone, copySiteAddress, dispatch, localContact, locationData }) => {
    return <div className='w-full pr-2'>
        <div className='flex w-full gap-1'>
            {currentMilestone.location.external_id.length > 0 && (
                <p className={`cursor-pointer truncate  text-font10 text-scogoorange`}>
                    Site Id : <span className={' w-full font-medium text-font11'}>{currentMilestone.location.external_id}</span>
                </p>
            )}

            {currentMilestone.location.external_id?.length === 0 && currentMilestone.location.branch?.name?.length === 0 && (
                <p className={`cursor-pointer truncate text-font10 text-scogoorange`}>Site Id :</p>
            )}

            {currentMilestone.location.external_id && (
                <span
                    className='cursor-pointer material-icons text-scogobgsky text-lg'
                    onClick={() => dispatch(copyToClipboard({ data: `${currentMilestone.location.external_id}` }))}
                >
                    content_copy
                </span>
            )}
        </div>
        <div>
            {currentMilestone?.location?.branch?.name && (currentMilestone?.location?.branch?.name !== currentMilestone.location.external_id) ? (
                <p className={`cursor-pointer truncate  text-font10 text-scogoorange`}>
                    Branch : <span className={'text-scogobgsky w-full text-font10'}>{currentMilestone?.location?.branch?.name}</span>
                </p>
            ) : null}
        </div>

        {localContact &&
            <div className='text-scogobgsky line-clamp-1 cursor-pointer text-left text-font10 hover:text-scogoorange'>
                <span className={'text-scogoorange'}>LC : </span>{localContact}
            </div>
        }

        <div className='w-full'>
            {currentMilestone.location.address &&
                <span className='text-font11 italic lien-clamp-2'>{currentMilestone.location.address}</span>
            }

            <div className='w-full flex '>
                {(locationData.city || locationData.pincode) ? <IconToolTip title='Pincode, City'>
                    <span className='text-white text-font11 max-w-8/12 truncate'>{`${locationData.pincode}, ${locationData.city}`}</span>
                </IconToolTip> : null}
                {(currentMilestone.location.address) && (
                    <>
                        <IconToolTip placement={'bottom'} title={`Open Location`}>
                            <span className='cursor-pointer material-icons text-scogobgsky text-lg' onClick={() => {
                                window.open(`https://maps.google.com?q=${`${currentMilestone.location.address}`}`, '_blank');
                            }}>
                                place
                            </span>
                        </IconToolTip>
                        <IconToolTip placement={'bottom'} title={`Copy Address`}>
                            <span
                                className='cursor-pointer material-icons text-scogobgsky text-lg'
                                onClick={() => copySiteAddress(currentMilestone.location.address)}
                            >
                                content_copy
                            </span>
                        </IconToolTip>
                    </>
                )}
                {locationData.tier ? (
                    <span className='w-4/12 truncate pl-1'>
                        <StatusLabel
                            titleText='City Tier'
                            labelClass={'bg-scogolightblue'}
                            content={locationData.tier}
                            color={cityTierClass[locationData.tier]}
                        />
                    </span>
                ) : null}
            </div>
        </div>
    </div>
};

const SiteForLogistics = ({ ticket, localContact, locationData, copySiteAddress }) => {
    const dispatch = useDispatch();

    const receive_location = useMemo(() => {
        return ticket?.milestones?.[0]?.receive_location || {};
    }, [ticket?.milestones]);

    const dispatch_location = useMemo(() => {
        return ticket?.milestones?.[0]?.dispatch_location || ticket?.location || {};
    }, [ticket?.milestones, ticket?.location]);

    return <div className='w-11/12'>

        {/* To */}
        <div className='flex w-full gap-1'>
            {receive_location.external_id && (
                <IconToolTip title={<div className=''>
                    <p className={`cursor-pointer text-font10 text-scogoorange`}>
                        External Id : <span className={' text-white w-full font-medium text-font11'}>{receive_location.external_id}</span>
                        <br />
                        Address : <span className={' text-font11 italic line-clamp-2 text-white'}>{receive_location.address}</span>
                    </p>
                </div>} placement='bottom'>
                    <p className={`cursor-pointer truncate  text-font10 text-black`}>
                        To : <span className={' text-black w-full font-medium text-font11'}>{receive_location.external_id}</span>
                    </p>
                </IconToolTip>
            )}

            {receive_location.external_id && (
                <IconToolTip placement={'bottom'} title={`Copy Site Id`}>
                    <span
                        className='cursor-pointer material-icons text-scogobgsky text-lg'
                        onClick={() => dispatch(copyToClipboard({ data: `${receive_location.external_id}` }))}
                    >
                        content_copy
                    </span>
                </IconToolTip>
            )}
        </div>

        {/* From */}
        <div className='flex w-full gap-1'>
            {dispatch_location.external_id && (
                <IconToolTip title={<div className=''>
                    <p className={`cursor-pointer text-font10 text-scogoorange`}>
                        External Id : <span className={' text-white w-full font-medium text-font11'}>{dispatch_location.external_id}</span>
                        <br />
                        Address : <span className={' text-white w-full font-medium text-font11'}>{dispatch_location.address}</span>
                    </p>
                </div>} placement='bottom'>
                    <p className={`cursor-pointer truncate  text-font10 text-black`}>
                        From : <span className={' text-black w-full font-medium text-font11'}>{dispatch_location.external_id}</span>
                    </p>
                </IconToolTip>
            )}

            {dispatch_location.external_id && (
                <IconToolTip placement={'bottom'} title={`Copy Site Id`}>
                    <span
                        className='cursor-pointer material-icons text-scogobgsky text-lg'
                        onClick={() => dispatch(copyToClipboard({ data: `${dispatch_location.external_id}` }))}
                    >
                        content_copy
                    </span>
                </IconToolTip>
            )}
        </div>

        {localContact &&
            <IconToolTip placement={'bottom'} title={localContact}>
                <div className='text-scogobgsky line-clamp-1 cursor-pointer text-left text-font10 hover:text-scogoorange'>
                    <span className={'text-scogoorange'}>LC : </span>{localContact}
                </div>
            </IconToolTip>
        }

        <div className='w-full'>
            {ticket?.location?.address && <IconToolTip placement={'bottom'} title={<SiteDetailsPopup currentMilestone={ticket} copySiteAddress={copySiteAddress} dispatch={dispatch} localContact={localContact} locationData={locationData} />}>
                <span className='text-scogogray text-font11 italic line-clamp-2'>
                    {ticket?.location?.address}
                </span>
            </IconToolTip>}

            <div className='w-full flex '>
                {(locationData.city || locationData.pincode) ? <IconToolTip title='Pincode, City'>
                    <span className='text-black text-font11 max-w-8/12 truncate'>{`${locationData.pincode}, ${locationData.city}`}</span>
                </IconToolTip> : null}
                {(ticket?.location?.address) && (
                    <>
                        <IconToolTip placement={'bottom'} title={`Open Location`}>

                            <span className='cursor-pointer material-icons text-scogobgsky text-lg' onClick={() => {
                                window.open(`https://maps.google.com?q=${`${ticket?.location?.address}`}`, '_blank');
                            }}>
                                place
                            </span>
                        </IconToolTip>
                        <IconToolTip placement={'bottom'} title={`Copy Address`}>
                            <span
                                className='cursor-pointer material-icons text-scogobgsky text-lg'
                                onClick={() => copySiteAddress(ticket?.location?.address)}
                            >
                                content_copy
                            </span>
                        </IconToolTip>
                    </>
                )}
                {locationData.tier && (
                    <span className='w-4/12 truncate '>
                        <StatusLabel
                            titleText='City Tier'
                            labelClass={'bg-scogolightblue'}
                            content={locationData.tier}
                            color={cityTierClass[locationData.tier]}
                        />
                    </span>
                )}
            </div>
        </div>
    </div>
}